import React, { useState, useEffect } from 'react';
import { getCities } from '../services/citiesService';
import {
  Typography,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Space,
  Alert,
  Spin,
  Select,
  Upload,
  message,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import CustomDateTimePicker from '../components/CustomDateTimePicker';
import { uploadImage } from '../services/uploadService';
import { EditOutlined, DeleteOutlined, PlusOutlined, CalendarOutlined } from '@ant-design/icons';
import {
  allEvents,
  createEvent,
  deleteEvent,
  getEvents,
  updateEvent,
} from '../services/eventsService';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { FaEyeSlash } from 'react-icons/fa';

function EventsAdmin() {
  const [events, setEvents] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [disabled, setDisabled] = useState(false);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState(null);
  const [form] = Form.useForm();
  const { Title } = Typography;

  const submittedCount = events.filter(event => event.status === 'submitted').length;

  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [eventsData, citiesData] = await Promise.all([allEvents(), getCities()]);
        setEvents(eventsData);
        setCities(citiesData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const fetchEvents = async () => {
    try {
      const data = await allEvents();
      setEvents(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (event = null) => {
    if (event) {
      setEditingEvent(event);
      form.setFieldsValue({
        name: event.name,
        description: event.description || '',
        ticket_url: event.ticket_url || '',
        image_url: event.image_url || '',
        start_date: moment(event.start_date),
        end_date: moment(event.end_date),
        status: event.status,
        city_id: cities[0].id,
        location: event.location || '',
      });
    } else {
      setEditingEvent(null);
      form.resetFields();
    }
    setOpenDialog(true);
  };

  const handleCloseModal = () => {
    setOpenDialog(false);
    setEditingEvent(null);
    form.resetFields();
  };

  const handleSubmit = async values => {
    console.log(values);
    values.city_id = cities[0].id;

    const eventData = {
      ...values,
      start_date: values.start_date.toISOString(),
      end_date: values.end_date.toISOString(),
    };

    setLoading(true);
    try {
      if (editingEvent) {
        await updateEvent(editingEvent.id, eventData);
      } else {
        await createEvent(eventData);
      }
      fetchEvents();
      handleCloseModal();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async id => {
    Modal.confirm({
      title: 'Are you sure you want to delete this event?',
      onOk: async () => {
        setLoading(true);
        try {
          await deleteEvent(id);
          fetchEvents();
        } catch (err) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Start Date',
      dataIndex: 'start_date',
      key: 'start_date',
      render: date => new Date(date).toLocaleDateString(),
      sorter: (a, b) => new Date(a.start_date) - new Date(b.start_date),
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: date => new Date(date).toLocaleDateString(),
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: (a, b) => a.status.localeCompare(b.status),
      render: status => {
        switch (status) {
          case 'active':
            return <span style={{ color: 'black' }}>Active</span>;
          case 'upcoming':
            return <span style={{ color: 'black' }}>Upcoming</span>;
          case 'cancelled':
            return <span style={{ color: 'black' }}>Cancelled</span>;
          case 'submitted':
            return (
              <span style={{ color: 'orange' }}>
                <FaEyeSlash /> Submitted
              </span>
            );
          case 'inactive':
            return (
              <span style={{ color: 'grey' }}>
                <FaEyeSlash /> Inactive
              </span>
            );
          case 'disabled':
            return (
              <span style={{ color: 'grey' }}>
                <FaEyeSlash /> Disabled
              </span>
            );
          default:
            return status;
        }
      },
    },

    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button type="text" icon={<EditOutlined />} onClick={() => handleOpenModal(record)} />
          <Button
            type="text"
            icon={<CalendarOutlined />}
            onClick={() => navigate(`/admin/events/${encodeURIComponent(record.name)}/related`)}
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          />
        </Space>
      ),
    },
  ];

  if (loading)
    return (
      <div style={{ textAlign: 'center', padding: '24px' }}>
        <Spin size="large" />
      </div>
    );
  if (error) return <Alert message={error} type="error" />;

  return (
    <div style={{ padding: '24px' }}>
      <div
        style={{
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Title level={2}>Events</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenModal()}>
          Add Event
        </Button>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Space>
          <Input.Search
            placeholder="Search events..."
            allowClear
            onChange={e => setSearchText(e.target.value)}
            style={{ width: 200 }}
          />
          <Select
            placeholder="Filter by status"
            allowClear
            onChange={value => setStatusFilter(value)}
            style={{ width: 200 }}
          >
            <Select.Option value="active">Active</Select.Option>
            <Select.Option value="upcoming">Upcoming</Select.Option>
            <Select.Option value="cancelled">Cancelled</Select.Option>
            <Select.Option value="submitted">Submitted</Select.Option>
            <Select.Option value="inactive">Inactive</Select.Option>
            <Select.Option value="disabled">Disabled</Select.Option>
          </Select>
          {submittedCount > 0 && (
            <Button type="primary" onClick={() => setStatusFilter('submitted')}>
              Submitted ({submittedCount})
            </Button>
          )}
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={events.filter(event => {
          const matchesSearch = searchText
            ? event.name.toLowerCase().includes(searchText.toLowerCase()) ||
              event.description?.toLowerCase().includes(searchText.toLowerCase())
            : true;

          const matchesStatus = statusFilter ? event.status === statusFilter : true;

          return matchesSearch && matchesStatus;
        })}
        rowKey="id"
      />

      <Modal
        title={editingEvent ? 'Edit Event' : 'Add Event'}
        open={openDialog}
        onCancel={handleCloseModal}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please input the event name!' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} />
          </Form.Item>

          <Form.Item name="location" label="Location">
            <Input />
          </Form.Item>
          <Form.Item hidden name="city_id" label="City">
            <Select>
              {cities &&
                cities.map(city => (
                  <Select.Option key={city.id} value={city.id}>
                    {city.name}, {city.state}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="start_date"
            label="Start Date & Time"
            rules={[{ required: true, message: 'Please select the start date and time!' }]}
          >
            <CustomDateTimePicker />
          </Form.Item>
          <Form.Item
            name="end_date"
            label="End Date & Time"
            rules={[{ required: true, message: 'Please select the end date and time!' }]}
          >
            <CustomDateTimePicker />
          </Form.Item>
          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: 'Please select a status!' }]}
          >
            <Select>
              <Select.Option value="active">Active</Select.Option>
              <Select.Option value="cancelled">Cancelled</Select.Option>
              <Select.Option value="inactive">
                <FaEyeSlash color="grey" /> Inactive
              </Select.Option>
              <Select.Option value="disabled">
                <FaEyeSlash color="grey" /> Disabled
              </Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="ticket_url" label="Ticket URL">
            <Input />
          </Form.Item>
          <Form.Item name="image_url" label="Image">
            <Upload
              maxCount={1}
              accept="image/*"
              beforeUpload={async file => {
                setDisabled(true);
                try {
                  const url = await uploadImage(file);
                  form.setFieldsValue({ image_url: url });
                  message.success('Image uploaded successfully');
                  setDisabled(false);
                } catch (err) {
                  message.error('Failed to upload image');
                }
                return false;
              }}
            >
              <Button icon={<UploadOutlined />}>Upload Image</Button>
            </Upload>
            {form.getFieldValue('image_url') && (
              <img
                src={form.getFieldValue('image_url')}
                alt="Preview"
                style={{
                  marginTop: 8,
                  maxWidth: '100%',
                  maxHeight: 200,
                  objectFit: 'contain',
                }}
              />
            )}
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleCloseModal}>Cancel</Button>
              <Button disabled={disabled} type="primary" htmlType="submit" loading={loading}>
                {editingEvent ? 'Update' : 'Create'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default EventsAdmin;
